/*
 *  Copyright (c) Qwoach LLC 2020. All rights reserved.
 *  Unauthorized copying of this file, via any medium is strictly prohibited
 *  Proprietary and confidential
 *  Created by Artem Bogush <artem@qwoach.com> on 7/10/2020
 */


import PersonItem from "@/core/js/model/Invoice/PersonItem";
import BillingData from "@/core/js/model/Billing/BillingData";
import {Type} from "class-transformer";
import {PaymentStatus} from "@/core/js/model/Billing/BillingItem";

import dayjs from 'dayjs'
import {copyObject} from "@/utils/helpers";
import CurrencyItem from "@shared/Profile/CurrencyItem";
import AddressItem from "@shared/Profile/AddressItem";
import {ObjectTypeEnum} from "./ObjectTypeEnum.js"
import RecurringData from "@shared/Invoice/RecurringData";
import FilterableItem from "@shared/Filterable/FilterableItem.js";

export const PaymentMethod = Object.freeze({
	NONE: "NONE",
	FREE: "FREE",
	MANUAL: "MANUAL",
	PAYPAL: "PAYPAL",
	STRIPE: "STRIPE",
	CLOVER: "CLOVER",
})

export const InvoiceCommand = Object.freeze({
	OK:"OK",
	CONFIRM: "CONFIRM",
	WITHDRAW: "WITHDRAW",
	REMOVE_PAID: "REMOVE_PAID",
	CREATE: "CREATE",
	CREATE_CONFIRMED: "CREATE_CONFIRMED",
})


export default class InvoiceItem extends FilterableItem{

	type: string = "InvoiceItem"

	billFromCoachId: string = null
	billFromTeamMemberId: string = null
	billToClientId: string = null

	@Type(() => PersonItem)
	billFrom: PersonItem = new PersonItem()

	@Type(() => PersonItem)
	billTo: PersonItem = new PersonItem()

	@Type(() => AddressItem)
	billingAddress: AddressItem = new AddressItem()

	@Type(() => BillingData)
	billingData: BillingData = new BillingData()

	billingItemIds : string[] = []

	isEmailSent: boolean = false
	paymentStatus: PaymentStatus = PaymentStatus.NONE
	paymentStatusPrev = PaymentStatus.NONE;

	tempDisposeAfter: number = null

	paidWith : PaymentMethod = PaymentMethod.NONE
	payPalCaptureId: string = null
	stripeClientReferenceId: string = null
	sentDateTs: number = 0
	paidDateTs: number = 0
	dueDateTs: number = 0

	targetObjectType: ObjectTypeEnum = null
	targetObjectId: string = null
	targetObjectParentId: string = null
	targetObjectTitle: string = null
	targetObjectExtras: Object = {}

	isRecurringInvoice: boolean = false
	@Type(() => RecurringData)
	recurringData: RecurringData = null
	validTillMs: number = -1 //-1 means indefinitely. Otherwise set the renewal date to indicate the validity period of the attached object


	programId: string = ""
	title: string = ""

	paymentInstructions: string = "";

	amountTotal: number = 0

	@Type(() => CurrencyItem)
	currencyItem: CurrencyItem = CurrencyItem.USD

	detailsUrl: string = ""

	webhookIds: string[] = []
	webhookIdsProcessing: string[] = []


	constructor(billFromCoachId: string = null, billToClientId: string = null) {
		super();
		this.billFromCoachId = billFromCoachId;
		this.billToClientId = billToClientId;
		this.setId(this.id)
	}

// constructor() {
	// 	super();
	// 	this.setId(this.id)
	// }

	setId(id){
		this.id = id
		if(id){
			this.shortId = this.id.substring(0, 6)
		}
	}

	setPaymentStatus(newStatus){
		console.log("InvoiceItem.setPaymentStatus: newStatus = ", newStatus)

		if(newStatus !== this.paymentStatus){
			this.paymentStatusPrev = this.paymentStatus || PaymentStatus.NONE;
			this.paymentStatus = newStatus || PaymentStatus.NONE
		}
	}
	revertPaymentStatus(){
		this.paymentStatus = this.paymentStatusPrev || PaymentStatus.NONE
		this.paymentStatusPrev = PaymentStatus.NONE;
	}


	setBillingData(billingData){
		this.billingData = billingData
		this.billingItemIds = [];
		console.log("setBillingData: ", billingData)
		billingData.billingItems.forEach(item => this.billingItemIds.push(item.id))
	}

	setPaid(paidDateTs, paidWith){
		console.log("InvoiceItem.setPaid: ", paidDateTs, paidWith, this)
		if(this.isRecurringInvoice && this.recurringData){
			this.recurringData.setPaid(paidDateTs, paidWith)
			this.setPaymentStatus(PaymentStatus.PARTIALLY_PAID)
			this.validTillMs = this.recurringData.getNextRenewalTs()
		}

		if(!this.isRecurringInvoice || this.recurringData.isFullyPaid()){
			this.setInvoiced()
			this.setPaymentStatus(PaymentStatus.PAID)
			this.validTillMs = -1
			if(paidDateTs){
				this.paidDateTs = paidDateTs
			}
			if(paidWith){
				this.paidWith = paidWith
			}
		}
	}
	setConfirmPending(paidDateTs){
		this.setPaymentStatus(PaymentStatus.CONFIRM_PENDING)
		this.paidDateTs = paidDateTs || new Date().getTime()
		this.paidWith = PaymentMethod.MANUAL
	}
	setTemp(duration = 10 * 60 * 1000){
		this.setPaymentStatus(PaymentStatus.TEMP)
		this.tempDisposeAfter = new Date().getTime() + duration
	}

	setFromPaymentOption(paymentOption){
		console.log("InvoiceItem.setFromPaymentOption: paymentOption = ", paymentOption)

		this.isRecurringInvoice = paymentOption.installmentsTotal > 1
		if(this.isRecurringInvoice){
			this.recurringData = new RecurringData()
			this.recurringData.installmentsTotal = paymentOption.installmentsTotal
			this.recurringData.installmentsPaid = 0
			this.recurringData.recurringAmount = paymentOption.singlePaymentAmount()
			this.recurringData.repeatInterval = paymentOption.repeatInterval
			this.recurringData.intervalLength = paymentOption.intervalLength
		} else {
			this.recurringData = null
		}
		this.amountTotal = paymentOption.totalPaymentAmount()
	}


	setInvoiced(invoicedDateTs){
		this.setPaymentStatus(PaymentStatus.INVOICED)
		if(invoicedDateTs){
			this.sentDateTs = invoicedDateTs
		}
		if(!this.sentDateTs){
			this.sentDateTs = new Date().getTime()
		}
		this.paidDateTs = 0
	}

	getInvoiceFor(){
		return this
	}


	setTargetObject(objectId, objectType, title, targetObjectParentId){
		this.targetObjectId = objectId
		this.targetObjectType = objectType
		if(targetObjectParentId){
			this.targetObjectParentId = targetObjectParentId
		}
		if(title){
			this.targetObjectTitle = title
		}
	}

	getTargetObjectExtraProp(key){
		return this.targetObjectExtras[key]
	}

	setTargetObjectExtraProps(keyValueObj){
		Object.keys(keyValueObj).forEach(key => {
			if(keyValueObj[key] === null || keyValueObj[key] === undefined){
				delete this.targetObjectExtras[key]
			} else {
				this.targetObjectExtras[key] = keyValueObj[key]
			}
		})
	}

	addTargetObjectExtraProps(keyValueObj){
		Object.keys(keyValueObj).forEach(key => {
			if(keyValueObj[key] !== null && keyValueObj[key] !== undefined){
				this.targetObjectExtras[key] = keyValueObj[key]
			}
		})
	}

	getTargetObjectExtraPropByKey(key){
		return this.targetObjectExtras[key]
	}


	setNone(){
		this.setDeleted()
	}

	setDeleted(){
		this.paymentStatus = PaymentStatus.DELETED
		this.paidDateTs = 0
		this.sentDateTs = 0
	}

	isPaymentStatusNone(){
		return this.paymentStatus === PaymentStatus.NONE
	}

	isDeleted(){
		return this.paymentStatus === PaymentStatus.DELETED || this.isPaymentStatusNone()
	}
	isInvoiced(){
		return this.paymentStatus === PaymentStatus.INVOICED
	}
	isPaid(){
		return this.paymentStatus === PaymentStatus.PAID
	}
	isPartiallyPaid(){
		return this.paymentStatus === PaymentStatus.PARTIALLY_PAID
	}
	isConfirmPending(){
		return this.paymentStatus === PaymentStatus.CONFIRM_PENDING
	}
	isTemp(){
		return this.paymentStatus === PaymentStatus.TEMP
	}



	paidDateFormatted(){
		return dayjs(this.paidDateTs).format("MMM DD, YYYY")
	}
	dueDateFormatted(){
		return dayjs(this.dueDateTs).format("MMM DD, YYYY")
	}
	invoiceSentDateFormatted(){
		return dayjs(this.sentDateTs).format("MMM DD, YYYY")
	}

	invalidate(invoiceItemGroup){
		this.billingData.invalidate([invoiceItemGroup])
		this.amountTotal = this.billingData.totalAmount()
	}

	getTotalAmount(){
		if(this.isRecurringInvoice){
			return this.amountTotal
		}
		return this.billingData.totalAmount()
	}
	getTotalAmountFormatted(){
		return this.currencyItem.formatted(this.getTotalAmount())
	}
	getAmountPaidFormatted(){
		return this.currencyItem.formatted(this.getAmountPaid())
	}

	getAmountPaid(){
		if(this.isRecurringInvoice){
			return this.recurringData.recurringAmount
		}
		return this.getTotalAmount()
	}
	getInstallmentsTotal(){
		if(this.isRecurringInvoice){
			return this.recurringData.installmentsTotal
		}
		return 1
	}
	// getCurrentAmount(){
	// 	return this.billingData.totalAmount()
	// }
	getRecurringTitle(){
		if(this.recurringData){
			return `${this.title} (${this.recurringData.installmentsTotal} payments totalling ${this.currencyItem.formatted(this.amountTotal)})`
		}
		return this.title
	}

	totalAmountFormatted(){
		return `${this.currencyItem.formatted(this.amountTotal)}`
	}
	amountFormatted(amount){
		amount = amount || this.amountTotal
		return `${this.currencyItem.formatted(amount)}`
	}



	static instanceWithFromTo(billingData, billFromCoachId, billToClientId, currencyItem) {
		let invoice = InvoiceItem.instance(billingData, null, null)
		invoice.billFromCoachId = billFromCoachId
		invoice.billToClientId = billToClientId
		invoice.addSharedWithById([billFromCoachId, billToClientId])
		if(currencyItem){
			invoice.currencyItem = currencyItem
		}
		return invoice
	}

	static instance(billingData: BillingData, selectedBillingItemIds: string[] = [], currencyItem) {
		let invoice = new InvoiceItem()
		if(currencyItem){
			invoice.currencyItem = currencyItem
		}
		console.log("createInvoiceFor: invoice.currencyItem = ", invoice.currencyItem)

		if(billingData){
			let billingDataCopy = copyObject(BillingData, billingData)
			if(selectedBillingItemIds && selectedBillingItemIds.length > 0){
				billingDataCopy.billingItems = selectedBillingItemIds.map(billingItemId => billingData.findBillingItemById(billingItemId))
			}
			invoice.setBillingData(billingDataCopy)
		}
		return invoice
	}

	hasWebhookId(webhookId){
		let index = this.webhookIds.findIndex(id => id === webhookId)
		return index >= 0
	}
	hasWebhookIdProcessing(webhookId){
		let index = this.webhookIdsProcessing.findIndex(id => id === webhookId)
		return index >= 0
	}
	addWebhookId(webhookId){
		if(webhookId){
			this.webhookIds.push(webhookId)
			this.removeWebhookIdProcessing(webhookId)
		}
	}

	addWebhookIdProcessing(webhookId){
		if(!webhookId) {
			return
		}
		let index = this.webhookIdsProcessing.findIndex(id => id === webhookId)
		if(index >= 0) {
			this.webhookIdsProcessing.push(webhookId)
		}

	}

	removeWebhookIdProcessing(webhookId){
		let index = this.webhookIdsProcessing.findIndex(id => id === webhookId)
		if(index >= 0){
			this.webhookIdsProcessing.splice(index, 1)
			return true
		}
		return false
	}


}




